import { useState, useRef } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { X, Loader2, CheckCircle, Shield, Sparkles, Tag } from 'lucide-react'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import api from '../lib/api'
import { useFocusTrap } from '../lib/useFocusTrap'
import { useAuthStore } from '../store/authStore'
import { googleAdsConversion } from '../lib/googleAnalytics'

interface PaymentModalProps {
  clientSecret: string
  stripeKey: string
  planName: string
  amount: number
  credit?: number
  fullPrice?: number
  voucherDiscount?: number
  currency?: string
  billing?: 'monthly' | 'annual'
  onSuccess: () => void
  onClose: () => void
}

export default function PaymentModal({ clientSecret, stripeKey, planName, amount, credit, fullPrice, voucherDiscount, currency, billing, onSuccess, onClose }: PaymentModalProps) {
  const { t, i18n } = useTranslation()
  const [stripePromise] = useState(() => loadStripe(stripeKey))
  const sym = currency === 'eur' ? '€' : 'Kč'
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(dialogRef, onClose)

  return (
    // Backdrop wrapper: click-outside-to-close (modal has a visible close button)
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Modal — onClick only stops backdrop close from firing inside it; not an interactive control */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-md animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Gradient glow behind modal */}
        <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 via-accent-500 to-violet-500 rounded-3xl opacity-20 blur-xl" />

        <div className="relative bg-gray-950 border border-white/10 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="relative px-6 pt-6 pb-4">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-white transition p-1"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/25">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">SEO Tools {planName}</h3>
                <p className="text-sm text-gray-400">{billing === 'annual' ? t('payment.annual_sub', { defaultValue: 'Roční předplatné' }) : t('payment.monthly_sub')}</p>
              </div>
            </div>

            {/* Price display */}
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-4xl font-extrabold text-white">{(amount / 100).toLocaleString()}</span>
              <span className="text-lg text-gray-400 font-medium">{sym}</span>
            </div>

            {(credit != null && credit > 0 || voucherDiscount != null && voucherDiscount > 0) && fullPrice && (
              <div className="space-y-0.5 mt-1">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <span className="line-through">{(fullPrice / 100).toLocaleString()} {sym}</span>
                </div>
                {credit != null && credit > 0 && (
                  <div className="text-xs text-blue-400">- {(credit / 100).toLocaleString()} {sym} {t('payment.credit_unused')}</div>
                )}
                {voucherDiscount != null && voucherDiscount > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-emerald-400">
                    <Tag className="w-3 h-3" />
                    - {(voucherDiscount / 100).toLocaleString()} {sym} {t('payment.voucher_code_label')}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
              <Shield className="w-3.5 h-3.5 text-emerald-500" />
              <span>{t('payment.secure_payment')}</span>
            </div>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

          {/* Stripe Elements */}
          <div className="px-6 py-5">
            {stripePromise && clientSecret && (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: {
                    theme: 'night',
                    variables: {
                      colorPrimary: '#6366f1',
                      colorBackground: '#18181b',
                      colorText: '#fafafa',
                      colorTextSecondary: '#a1a1aa',
                      colorTextPlaceholder: '#52525b',
                      colorDanger: '#ef4444',
                      fontFamily: 'Plus Jakarta Sans, system-ui, sans-serif',
                      borderRadius: '10px',
                      spacingUnit: '4px',
                      fontSizeBase: '14px',
                    },
                    rules: {
                      '.Input': {
                        backgroundColor: '#0f0f23',
                        border: '1px solid rgba(255,255,255,0.1)',
                        boxShadow: 'none',
                        padding: '12px 14px',
                      },
                      '.Input:focus': {
                        border: '1px solid #6366f1',
                        boxShadow: '0 0 0 3px rgba(99,102,241,0.15)',
                      },
                      '.Label': {
                        color: '#a1a1aa',
                        fontSize: '13px',
                        fontWeight: '500',
                        marginBottom: '6px',
                      },
                      '.Tab': {
                        backgroundColor: '#0f0f23',
                        border: '1px solid rgba(255,255,255,0.08)',
                      },
                      '.Tab--selected': {
                        backgroundColor: '#1e1b4b',
                        border: '1px solid #6366f1',
                      },
                    },
                  },
                  locale: i18n.language as any,
                }}
              >
                <CheckoutForm onSuccess={onSuccess} planName={planName} amount={amount} currency={currency} />
              </Elements>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function CheckoutForm({ onSuccess, planName, amount, currency }: { onSuccess: () => void; planName: string; amount: number; currency?: string }) {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return

    setLoading(true)
    setError('')

    const { error: submitError } = await elements.submit()
    if (submitError) {
      setError(submitError.message || t('payment.process_error'))
      setLoading(false)
      return
    }

    const result = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin + '/dashboard?payment=success&plan=' + planName.toLowerCase(),
      },
      redirect: 'if_required',
    })

    if (result.error) {
      setError(result.error.message || t('payment.payment_failed'))
      setLoading(false)
      return
    }

    // Payment succeeded — tell backend to activate the subscription
    try {
      const paymentIntentId = result.paymentIntent?.id
      if (paymentIntentId) {
        const { data: conf } = await api.post('/subscription/confirm-payment', {
          payment_intent_id: paymentIntentId,
          plan: planName.toLowerCase(),
        })
        // Guest checkout — backend vrátí token nově založeného účtu → rovnou přihlásit.
        if (conf?.token) { await useAuthStore.getState().loginWithToken(conf.token) }
        // Google Ads konverze: úspěšná platba předplatného (transaction_id = dedup).
        googleAdsConversion('purchase', {
          value: amount,
          currency: currency === 'eur' ? 'EUR' : 'CZK',
          transactionId: paymentIntentId,
        })
      }
      setSuccess(true)
      setTimeout(onSuccess, 1500)
    } catch (err: any) {
      // Payment went through but activation failed — show partial success
      setError(t('payment.activation_failed'))
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-emerald-400" />
        </div>
        <h4 className="text-xl font-bold text-white mb-2">{t('payment.success')}</h4>
        <p className="text-gray-400 text-sm">{t('payment.activated', { plan: planName })}</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement />

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-2.5 rounded-xl text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || loading}
        className={clsx(
          'w-full py-3.5 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2',
          loading
            ? 'bg-gray-700 cursor-not-allowed'
            : 'bg-gradient-to-r from-primary-600 to-primary-500 hover:shadow-lg hover:shadow-primary-500/25'
        )}
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> {t('payment.processing')}</>
        ) : (
          <>{t('payment.checkout_activate', { plan: planName, defaultValue: 'Zaplatit a aktivovat {{plan}}' })}</>
        )}
      </button>
    </form>
  )
}
